import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import classNames from "classnames"

import { ID } from "../../../../../model/types"
import IdeaBox from "../../../../components/idea_box"
import { useNana } from "../../../../hooks/useNana"
import { MosaicPageState } from "../../mosaic"
import { polishGaps, PolishGap } from "../eligibility"
import MoveCard, {
  CardMeta,
  CardPreviewText,
  CardPrimaryButton,
  previewFromHtml,
} from "../move_card"

const GAP_LABEL: Record<PolishGap, string> = {
  title: "No title",
  content: "No content",
  source: "No source",
}

const textShouldBeWhiteOrBlack = (bgColour: string) => {
  const r = parseInt(bgColour.slice(1, 3), 16)
  const g = parseInt(bgColour.slice(3, 5), 16)
  const b = parseInt(bgColour.slice(5, 7), 16)
  const brightness = Math.sqrt(0.299 * r * r + 0.587 * g * g + 0.114 * b * b)
  return brightness < 140
}

export default function PolishCard({
  ideaId,
  onReroll,
  onObjectGone,
}: {
  ideaId: ID
  onReroll: () => void
  onObjectGone: () => void
}) {
  const { model } = useNana()
  const navigate = useNavigate()
  const idea = model.getIdea(ideaId)
  const gaps = idea ? polishGaps(idea) : []

  useEffect(() => {
    if (!idea || gaps.length === 0) onObjectGone()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idea, gaps.length])

  if (!idea || gaps.length === 0) return null

  const open = () => {
    MosaicPageState.forceOpenLocation = idea.location
    navigate("/app")
  }

  const lightText = idea.colour ? textShouldBeWhiteOrBlack(idea.colour) : false
  const body = previewFromHtml(idea.content, 280)
  const titleText =
    idea.title?.trim() ||
    (body ? body.slice(0, 80) : null) ||
    idea.memo ||
    "Untitled idea"

  return (
    <MoveCard
      move="polish"
      onReroll={onReroll}
      onPrimary={open}
      actions={
        <CardPrimaryButton onClick={open}>Open on mosaic</CardPrimaryButton>
      }
    >
      <div className="flex min-h-[7rem] flex-1 flex-col gap-3">
        <div
          className={classNames(
            "flex items-center gap-3 overflow-hidden rounded-md px-3.5 py-3",
            lightText ? "text-white" : "text-stone-900",
          )}
          style={{
            background: idea.colour
              ? `linear-gradient(135deg, ${idea.colour} 0%, ${idea.colour}cc 100%)`
              : "linear-gradient(135deg, #e7e5e4 0%, #d6d3d1 100%)",
          }}
        >
          <IdeaBox idea={idea} shadow={false} />
          <div className="min-w-0">
            <div className="line-clamp-2 text-[1.05rem] font-semibold leading-snug tracking-[-0.015em]">
              {idea.title?.trim() ? (
                idea.title
              ) : (
                <span className="italic opacity-90">{titleText}</span>
              )}
            </div>
            {idea.type === "goal" && (
              <div className="mt-0.5 text-[11px] font-medium uppercase tracking-[0.14em] opacity-75">
                Goal
              </div>
            )}
          </div>
        </div>

        {body && (
          <CardPreviewText lines={3}>{body}</CardPreviewText>
        )}

        <div className="flex flex-wrap gap-1.5">
          {gaps.map((gap) => (
            <span
              key={gap}
              className="inline-flex items-center rounded-full bg-teal-50 px-2.5 py-0.5 text-[11px] font-medium text-teal-900 ring-1 ring-inset ring-teal-900/10 dark:bg-teal-950/50 dark:text-teal-100 dark:ring-teal-100/15"
            >
              {GAP_LABEL[gap]}
            </span>
          ))}
        </div>

        <CardMeta>
          <span className="opacity-70">Fill in what’s missing</span>
        </CardMeta>
      </div>
    </MoveCard>
  )
}
